import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { IoReloadOutline } from "react-icons/io5";
import SectionHeader from "../../components/sectionHeader/sectionHeader";
import Card from "../../components/card/card";
import InputSearch from "../../components/inputs/inputSearch/inputSearch";
import Button from "../../components/button/button";
import { getApiInstance } from "../../utils/api";
import { searchByKeywords } from "../../utils/searchUtils";

const SUBCATEGORIA_CODIGOS = {
    munich: [9],
    copenhague: [10],
    miami: [11],
    oxford: [13],
    "make-up-400": [8],
    todo: [],
};

const CATEGORIAS_MAP = {
    8: "Make Up 400",
    9: "Munich",
    10: "Copenhague",
    11: "Miami-Splau",
    13: "Oxford",
};

export default function CatalogoFamilia({ title, className, codFamilia }) {
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const { subcategoria } = useParams();
    const [visibleCount, setVisibleCount] = useState(15);
    const [searchTerm, setSearchTerm] = useState("");
    const [searchParams, setSearchParams] = useSearchParams();
    const [allGlobal, setAllGlobal] = useState(null);

    useEffect(() => {
        const term = searchParams.get("q") || "";
        setSearchTerm(term);
    }, [searchParams]);

    useEffect(() => {
        const loadAll = async () => {
            if (!searchTerm || allGlobal) return;
            try {
                const api = getApiInstance();
                const res = await api.get("/api/Mueble/GetMueblesCatalogo");
                setAllGlobal(res.data);
            } catch (e) {
                console.error("Error cargando catálogo global", e);
            }
        };
        loadAll();
    }, [searchTerm, allGlobal]);

    const fetchProducts = useCallback(
        async (subcategoriaKey) => {
            setLoading(true);
            const api = getApiInstance();
            const codsCategoria =
                SUBCATEGORIA_CODIGOS[subcategoriaKey] || [];

            try {
                let productsData = [];

                if (subcategoriaKey === "todo") {
                    const response = await api.get(
                        `/api/Mueble/GetMueblesByFamilia/${codFamilia}`
                    );
                    productsData = response.data;
                } else {
                    const endpointBase = `/api/Mueble/GetMueblesByFamiliaCategoria?codFamilia=${codFamilia}&codCategoria=`;
                    const promises = codsCategoria.map((cod) =>
                        api
                            .get(`${endpointBase}${cod}`)
                            .catch((err) =>
                                console.error(
                                    `Error fetching category ${cod}:`,
                                    err
                                )
                            )
                    );

                    const responses = await Promise.all(promises);
                    productsData = responses.flatMap((response) =>
                        response ? response.data : []
                    );
                }

                setProducts(productsData);
                setVisibleCount(15);
                setError(null);
            } catch (err) {
                setError(
                    "No se han podido cargar los muebles. Vuelve a intentarlo más tarde."
                );
                setProducts([]);
                console.error(err);
            } finally {
                setLoading(false);
            }
        },
        [codFamilia]
    );

    useEffect(() => {
        fetchProducts(subcategoria);
    }, [fetchProducts, subcategoria]);

    const handleSearch = (term) => {
        setSearchTerm(term);
        setVisibleCount(15);
        setSearchParams((prev) => {
            const newParams = new URLSearchParams(prev);
            if (term) {
                newParams.set("q", term);
            } else {
                newParams.delete("q");
            }
            return newParams;
        });
    };

    const visibleProducts = useMemo(() => {
        if (!searchTerm) return products;
        const base = allGlobal ?? products;
        return searchByKeywords(base, searchTerm, [
            "referencia",
            "nombreBook",
            "nombreFamilia",
        ]);
    }, [searchTerm, products, allGlobal]);

    if (loading)
        return (
            <div className="muebles-pages__error-message">
                Cargando muebles...
            </div>
        );
    if (error)
        return (
            <div className="muebles-pages__error-message">Error: {error}</div>
        );

    return (
        <div className={className}>
            <SectionHeader title={`${title} - ${subcategoria || "Todo"}`} />

            <div className={`${className}__header`}>
                <div className={`${className}__header__search`}>
                    <InputSearch
                        context="tableSearch"
                        value={searchTerm}
                        onSearch={handleSearch}
                        placeholder="Buscar productos..."
                    />
                </div>
            </div>

            <div className={`${className}__cards`}>
                {visibleProducts.length > 0 ? (
                    <>
                        {visibleProducts
                            .slice(0, visibleCount)
                            .map((product) => (
                                <Card
                                    key={product.codMueble}
                                    title={product.nombreBook}
                                    subtitle={product.nombreFamilia}
                                    reference={product.referencia}
                                    codCategoria={
                                        product.codCategorias &&
                                        product.codCategorias.length > 0
                                            ? product.codCategorias
                                                  .map(
                                                      (codigo) =>
                                                          CATEGORIAS_MAP[
                                                              codigo
                                                          ] || codigo
                                                  )
                                                  .join(", ")
                                            : product.categorias &&
                                              product.categorias.length > 0
                                            ? product.categorias.join(", ")
                                            : null
                                    }
                                    image={product.rutaImagen}
                                    link={`/ficha-producto/${product.codMueble}`}
                                    pendienteAprobacion={
                                        product.pendienteDeAprobacion
                                    }
                                    showButton={true}
                                />
                            ))}

                        {visibleCount < visibleProducts.length && (
                            <div className="mobiliario-central__load-more-container">
                                <Button
                                    label="Cargar más"
                                    variant="filled"
                                    icon={<IoReloadOutline />}
                                    onClick={() =>
                                        setVisibleCount((prev) => prev + 15)
                                    }
                                />
                            </div>
                        )}
                    </>
                ) : (
                    <p className={`${className}__cards__no-results-message`}>
                        No hay muebles en esta categoría
                    </p>
                )}
            </div>
        </div>
    );
}
